/**
 * Letterhead Service
 *
 * Handles letterhead prefetching and caching
 * Extracted from workflow-data.service.ts
 */

import { getLawFirmProfileAction } from '@/lib/services/account/actions';

/**
 * Prefetch law firm letterhead for immediate use
 * This ensures the letterhead is cached and ready when users open letter editors
 */
export async function prefetchLetterhead(): Promise<string | null> {
  try {
    // Fetch the law firm profile to get the letterhead file ID
    const profileResult = await getLawFirmProfileAction();

    if (!profileResult.success || !profileResult.data) {
      console.warn(
        '[LetterheadService] Could not fetch law firm profile for letterhead'
      );
      return null;
    }

    const profile = profileResult.data;

    if (!profile.letterHeadFileId) {
      console.log('[LetterheadService] No letterhead configured for law firm');
      return null;
    }

    // Generate the letterhead URL
    const letterheadUrl = `/api/files/${profile.letterHeadFileId}/download?preview=true&image=true`;

    // Prefetch the letterhead image to populate browser cache
    // This makes it immediately available when letter editors are opened
    console.log('[LetterheadService] Prefetching letterhead:', letterheadUrl);

    // Only run in browser environment
    if (typeof window === 'undefined') {
      return letterheadUrl;
    }

    try {
      const img = new Image();
      img.crossOrigin = 'use-credentials';

      await new Promise<void>((resolve, reject) => {
        img.onload = () => {
          console.log('[LetterheadService] Letterhead prefetched successfully');
          resolve();
        };
        img.onerror = (error) => {
          console.warn(
            '[LetterheadService] Failed to prefetch letterhead:',
            error
          );
          reject(error);
        };
        img.src = letterheadUrl;
      });

      return letterheadUrl;
    } catch (_imgError) {
      console.warn(
        '[LetterheadService] Image prefetch failed, URL still returned:',
        letterheadUrl
      );
      return letterheadUrl;
    }
  } catch (error) {
    console.error('[LetterheadService] Error prefetching letterhead:', error);
    return null;
  }
}
